import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { Coffee } from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../theme/ThemeProvider';

/**
 * Buy Me a Coffee button component
 * @param {string} username - Your Buy Me a Coffee username (without the @)
 * @param {string} variant - 'button' | 'floating' | 'inline'
 * @param {string} size - 'small' | 'medium' | 'large'
 */
const BuyMeACoffee = ({
    username = 'textsimpler',
    variant = 'button',
    size = 'medium',
    showText = true,
}) => {
    const theme = useTheme();
    const { darkMode } = useCustomTheme();

    const bmcUrl = `https://www.buymeacoffee.com/${username}`;

    const coffeeYellow = '#FFDD00';
    const coffeeHover = '#FFE94D';

    const sizes = {
        small: { py: 0.75, px: 2, fontSize: '0.85rem', iconSize: '1rem' },
        medium: { py: 1, px: 2.5, fontSize: '0.95rem', iconSize: '1.2rem' },
        large: { py: 1.25, px: 3, fontSize: '1rem', iconSize: '1.4rem' },
    };

    const currentSize = sizes[size] || sizes.medium;

    if (variant === 'floating') {
        return (
            <Box
                component="a"
                href={bmcUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: coffeeYellow,
                    color: '#000',
                    py: 1.25,
                    px: 2.5,
                    borderRadius: '50px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        background: coffeeHover,
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
                    },
                }}
            >
                <Coffee sx={{ fontSize: '1.3rem' }} />
                <span>Buy me a coffee</span>
            </Box>
        );
    }

    if (variant === 'inline') {
        return (
            <Box
                component="a"
                href={bmcUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    textDecoration: 'none',
                    fontSize: currentSize.fontSize,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        color: coffeeYellow,
                    },
                }}
            >
                <Coffee sx={{ fontSize: currentSize.iconSize }} />
                {showText && <span>Support us</span>}
            </Box>
        );
    }

    // Default button variant
    return (
        <Button
            component="a"
            href={bmcUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: coffeeYellow,
                color: '#000',
                py: currentSize.py,
                px: currentSize.px,
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: currentSize.fontSize,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'all 0.2s ease',
                '&:hover': {
                    background: coffeeHover,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                },
            }}
        >
            <Coffee sx={{ fontSize: currentSize.iconSize }} />
            {showText && 'Buy me a coffee'}
        </Button>
    );
};

export default BuyMeACoffee;
